/* ═══════════════════════════════════════════════════════════════
   SYNC — word tokens. Text is WORDS separated by GAPS (whitespace).
   Only words take part in a merge, so a whitespace-only difference is
   never a change; the gaps are kept so applying a change does not
   mangle the copy's own spacing. Pure.
   ═══════════════════════════════════════════════════════════════ */

export interface Tokens {
  words: string[];
  /** gaps[i] precedes words[i]; gaps[words.length] trails. Always words.length + 1. */
  gaps: string[];
}

export function tokenize(text: string): Tokens {
  const words: string[] = [];
  const gaps: string[] = [];
  let gap = '';
  const parts = (text || '').split(/(\s+)/);
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (i % 2 === 1) {
      gap += part;
    } else if (part !== '') {
      gaps.push(gap);
      words.push(part);
      gap = '';
    }
  }
  gaps.push(gap);
  return { words, gaps };
}

export function untokenize(t: Tokens): string {
  let out = t.gaps[0] ?? '';
  for (let i = 0; i < t.words.length; i++) out += t.words[i] + (t.gaps[i + 1] ?? '');
  return out;
}

/** Words [start, end) with the gaps between them — no outer whitespace. */
export function spanText(t: Tokens, start: number, end: number): string {
  let out = '';
  for (let i = start; i < end; i++) {
    out += t.words[i];
    if (i < end - 1) out += t.gaps[i + 1];
  }
  return out;
}

/** Replace words [start, end) of `t` with the words of `insert` (its own
 *  inner spacing kept, its outer whitespace ignored). Returns a new Tokens. */
export function spliceWords(t: Tokens, start: number, end: number, insert: Tokens): Tokens {
  const words = t.words.slice();
  const gaps = t.gaps.slice();
  const x = insert.words;
  const inner = insert.gaps.slice(1, Math.max(1, insert.gaps.length - 1));
  const k = x.length;
  const n = words.length;
  if (k === 0) {
    if (end <= start) return { words, gaps };
    words.splice(start, end - start);
    gaps.splice(start > 0 ? start : 1, end - start);
    return { words, gaps };
  }
  if (start === end) {
    if (n === 0) return { words: x.slice(), gaps: [gaps[0] ?? '', ...inner, ''] };
    if (start < n) {
      words.splice(start, 0, ...x);
      gaps.splice(start + 1, 0, ...inner, ' ');
    } else {
      words.push(...x);
      gaps.splice(n, 0, ' ', ...inner);
    }
    return { words, gaps };
  }
  words.splice(start, end - start, ...x);
  gaps.splice(start + 1, end - start - 1, ...inner);
  return { words, gaps };
}
